import { call, fork, put, select, takeEvery } from 'redux-saga/effects';
import { v4 as uuidv4 } from 'uuid';
import { stopSubmit, touch } from 'redux-form';
import { notificationInit } from '../../modules/notifications/actions';
import { THERMOBOX_REPORT_FORM_NAME, REQUEST_ERROR_MESSAGE, FIELDS_FOR_VALIDATE } from './constants';
import {
	createThermoboxReportRequest,
	deleteThermoboxReportRequest,
	getIcepacksFilledRequest,
	getModesDictionaryRequest,
	getThermoboxesRequest,
	getThermoboxReportDocsFormRequest,
	getThermoboxReportDocsRequest,
	getThermoboxReportInfoRequest,
	setThermoboxReportDocsRequest,
	updateThermoboxReportRequest,
} from '../../api/requests';
import {
	GET_THERMOBOX_REPORT_INFO,
	setThermoboxReportInfoAction,
	setIsThermoboxReportInfoLoadingAction,
	CREATE_THERMOBOX_REPORT,
	setIsThermoboxReportCreatingAction,
	UPDATE_THERMOBOX_REPORT,
	setIsThermoboxReportUpdatingAction,
	DELETE_THERMOBOX_REPORT,
	setIsThermoboxReportDeletingAction,
	GET_MODES,
	setModesAction,
	setIsModesLoadingAction,
	setIsThermoboxesLoadingAction,
	setThermoboxesAction,
	GET_THERMOBOXES,
	setIsIcepacksLoadingAction,
	setIcepacksAction,
	GET_ICEPACKS,
	GET_THERMOBOX_REPORT_DOCS,
	POST_THERMOBOX_REPORT_DOCS,
	setThermoboxReportDocsAction,
	setDocsFilesAction,
	setThermoboxReportDocsErrorsAction,
	setIsThermoboxReportDocsSendingAction,
	setIsThermoboxReportDocsLoadingAction,
	GET_THERMOBOX_REPORT_FORM,
	setThermoboxReportFormAction,
} from './actions';
import { DISMISS_TIME } from '../../api/constants';
import { errorHandler } from '../../api/utils';
import { getDataForSaveDocsSelector, getDataForSaveSelector, getFormIsInvalidSelector } from './selectors';
import { transformToValidationErrors } from './utils';
import { getThermoboxesReportsAction } from '../thermoboxes-reports/actions';

export function* createThermoboxReportSaga({ payload: { redirect, searchQuery } }) {
	try {
		yield put(setIsThermoboxReportCreatingAction(true));

		const dataForSave = yield select(getDataForSaveSelector());

		const isInvalid = yield select(getFormIsInvalidSelector());

		if (isInvalid) return;

		const { data, errors, message, toast } = yield call(createThermoboxReportRequest, dataForSave);

		if (data) {
			if (toast) {
				yield put(
					notificationInit({
						id: uuidv4(),
						dismissAfter: DISMISS_TIME,
						...toast,
						...(toast?.link ? { link: `${toast?.link}${searchQuery ? `&${searchQuery}` : ''}` } : {}),
					}),
				);
			}

			redirect();

			yield put(getThermoboxesReportsAction({ searchQuery, noLoading: true }));
		} else {
			if (errors) {
				yield put(touch(THERMOBOX_REPORT_FORM_NAME, ...FIELDS_FOR_VALIDATE));
				yield put(stopSubmit(THERMOBOX_REPORT_FORM_NAME, transformToValidationErrors(errors)));
			}

			throw new Error(message || REQUEST_ERROR_MESSAGE);
		}
	} catch (error) {
		yield fork(errorHandler, error);
	} finally {
		yield put(setIsThermoboxReportCreatingAction(false));
	}
}

export function* getThermoboxReportInfoSaga({ payload: id }) {
	try {
		yield put(setIsThermoboxReportInfoLoadingAction(true));

		const { data, message, toast } = yield call(getThermoboxReportInfoRequest, id);

		if (data) {
			if (toast) {
				yield put(notificationInit({ id: uuidv4(), dismissAfter: DISMISS_TIME, ...toast }));
			}
			yield put(setThermoboxReportInfoAction(data));
		} else {
			throw new Error(message || REQUEST_ERROR_MESSAGE);
		}
	} catch (error) {
		yield fork(errorHandler, error);
	} finally {
		yield put(setIsThermoboxReportInfoLoadingAction(false));
	}
}

export function* updateThermoboxReportSaga({ payload: { id, redirect, searchQuery } }) {
	try {
		yield put(setIsThermoboxReportUpdatingAction(true));

		const isInvalid = yield select(getFormIsInvalidSelector());

		if (isInvalid) return;

		const dataForSave = yield select(getDataForSaveSelector());

		const { data, errors, message, toast } = yield call(updateThermoboxReportRequest, id, dataForSave);

		if (data) {
			if (toast) {
				yield put(
					notificationInit({
						id: uuidv4(),
						dismissAfter: DISMISS_TIME,
						...toast,
						...(toast?.link ? { link: `${toast?.link}${searchQuery ? `&${searchQuery}` : ''}` } : {}),
					}),
				);
			}

			yield put(setThermoboxReportInfoAction(data));

			redirect();

			yield put(getThermoboxesReportsAction({ searchQuery, noLoading: true }));
		} else {
			if (errors) {
				yield put(touch(THERMOBOX_REPORT_FORM_NAME, ...FIELDS_FOR_VALIDATE));
				yield put(stopSubmit(THERMOBOX_REPORT_FORM_NAME, transformToValidationErrors(errors)));
			}

			throw new Error(message || REQUEST_ERROR_MESSAGE);
		}
	} catch (error) {
		yield fork(errorHandler, error);
	} finally {
		yield put(setIsThermoboxReportUpdatingAction(false));
	}
}

export function* deleteThermoboxReportSaga({ payload: { id, redirect, searchQuery } }) {
	try {
		yield put(setIsThermoboxReportDeletingAction(true));

		const { data, message, toast } = yield call(deleteThermoboxReportRequest, id);

		if (data) {
			if (toast) {
				yield put(notificationInit({ id: uuidv4(), dismissAfter: DISMISS_TIME, ...toast }));
			}

			redirect();

			yield put(getThermoboxesReportsAction({ searchQuery, noLoading: true }));
		} else {
			throw new Error(message || REQUEST_ERROR_MESSAGE);
		}
	} catch (error) {
		yield fork(errorHandler, error);
	} finally {
		yield put(setIsThermoboxReportDeletingAction(false));
	}
}

export function* getThermoboxesSaga() {
	try {
		yield put(setIsThermoboxesLoadingAction(true));

		const { data, message, toast } = yield call(getThermoboxesRequest, '?receive=all');

		if (data) {
			if (toast) {
				yield put(notificationInit({ id: uuidv4(), dismissAfter: DISMISS_TIME, ...toast }));
			}

			yield put(setThermoboxesAction(data));
		} else {
			throw new Error(message || REQUEST_ERROR_MESSAGE);
		}
	} catch (error) {
		yield fork(errorHandler, error);
	} finally {
		yield put(setIsThermoboxesLoadingAction(false));
	}
}

export function* getModesSaga() {
	try {
		yield put(setIsModesLoadingAction(true));

		const { data, message, toast } = yield call(getModesDictionaryRequest, '?receive=all');

		if (data) {
			if (toast) {
				yield put(notificationInit({ id: uuidv4(), dismissAfter: DISMISS_TIME, ...toast }));
			}

			yield put(setModesAction(data));
		} else {
			throw new Error(message || REQUEST_ERROR_MESSAGE);
		}
	} catch (error) {
		yield fork(errorHandler, error);
	} finally {
		yield put(setIsModesLoadingAction(false));
	}
}

export function* getIcepacksSaga() {
	try {
		yield put(setIsIcepacksLoadingAction(true));

		const { data, message, toast } = yield call(getIcepacksFilledRequest, '?receive=all');

		if (data) {
			if (toast) {
				yield put(notificationInit({ id: uuidv4(), dismissAfter: DISMISS_TIME, ...toast }));
			}

			yield put(setIcepacksAction(data));
		} else {
			throw new Error(message || REQUEST_ERROR_MESSAGE);
		}
	} catch (error) {
		yield fork(errorHandler, error);
	} finally {
		yield put(setIsIcepacksLoadingAction(false));
	}
}

function* getThermoboxReportDocs({ payload: id }) {
	try {
		yield put(setIsThermoboxReportDocsLoadingAction(true));

		const response = yield call(getThermoboxReportDocsRequest, id);

		if (response.data) {
			yield put(setThermoboxReportDocsAction(response.data));
		} else {
			throw new Error(response.message || REQUEST_ERROR_MESSAGE);
		}
	} catch (error) {
		yield fork(errorHandler, error);
	} finally {
		yield put(setIsThermoboxReportDocsLoadingAction(false));
	}
}

function* getThermoboxReportForm({ payload: id }) {
	try {
		yield put(setIsThermoboxReportDocsLoadingAction(true));

		const response = yield call(getThermoboxReportDocsFormRequest, id);

		if (response.data) {
			yield put(setThermoboxReportFormAction(response.data));
		} else {
			throw new Error(response.message || REQUEST_ERROR_MESSAGE);
		}
	} catch (error) {
		yield fork(errorHandler, error);
	} finally {
		yield put(setIsThermoboxReportDocsLoadingAction(false));
	}
}

function* postThermoboxReportDocs({ payload: { id, callback } }) {
	try {
		yield put(setIsThermoboxReportDocsSendingAction(true));

		const dataForSave = yield select(getDataForSaveDocsSelector());

		const response = yield call(setThermoboxReportDocsRequest, id, dataForSave);

		if (response.data) {
			yield put(setDocsFilesAction({}));

			if (response.toast) {
				yield put(notificationInit({ id: uuidv4(), dismissAfter: DISMISS_TIME, ...response.toast }));
			}

			callback();
		} else {
			if (response.errors) {
				yield put(setThermoboxReportDocsErrorsAction(transformToValidationErrors(response.errors)));
			}
			throw new Error(response.message || REQUEST_ERROR_MESSAGE);
		}
	} catch (error) {
		yield fork(errorHandler, error);
	} finally {
		yield put(setIsThermoboxReportDocsSendingAction(false));
	}
}

export default function* icepackModalSaga() {
	yield takeEvery(GET_THERMOBOX_REPORT_INFO, getThermoboxReportInfoSaga);
	yield takeEvery(CREATE_THERMOBOX_REPORT, createThermoboxReportSaga);
	yield takeEvery(UPDATE_THERMOBOX_REPORT, updateThermoboxReportSaga);
	yield takeEvery(DELETE_THERMOBOX_REPORT, deleteThermoboxReportSaga);
	yield takeEvery(GET_THERMOBOXES, getThermoboxesSaga);
	yield takeEvery(GET_MODES, getModesSaga);
	yield takeEvery(GET_ICEPACKS, getIcepacksSaga);
	yield takeEvery(GET_THERMOBOX_REPORT_DOCS, getThermoboxReportDocs);
	yield takeEvery(POST_THERMOBOX_REPORT_DOCS, postThermoboxReportDocs);
	yield takeEvery(GET_THERMOBOX_REPORT_FORM, getThermoboxReportForm);
}
